<script setup lang="ts">
import { computed, unref, toRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import CheckTaskDetailsTitle from '@/components/CheckTaskDetailsTitle.vue';
import TaskAttachments from '@/components/TaskAttachments.vue';
import TaskChecklist from '@/components/TaskChecklist.vue';
import TaskMeta from '@/components/TaskMeta.vue';
import { useYardTask, useTaskQueries } from '@/composables';
import useNotify from '@/composables/useNotify';
import { isTaskBlocked, isTaskInProgress } from '@satellite/helpers/yardTaskUtils';

import type { CheckTaskViewData, YardTaskChecklist } from '@nova/core';

const props = defineProps<{
  task: CheckTaskViewData;
}>();

const emit = defineEmits<{
  'allow-complete': [allow: boolean];
}>();

const task = toRef(props, 'task');
const { t } = useI18n();
const { notify } = useNotify();

const { taskDescription, getTaskChecklist, getTaskTypeLabel, isUrgent } = useYardTask({
  initialTask: task
});

const taskTypeLabel = computed(() => getTaskTypeLabel());
const taskChecklist = computed(() => getTaskChecklist());
const { useUpdateTaskMutation } = useTaskQueries();

const updateTaskMutation = useUpdateTaskMutation();

const isLoading = computed(() => Boolean(unref(updateTaskMutation.isPending)));

watch(
  [() => props.task?.status, () => props.task?.tags],
  () => emit('allow-complete', isTaskInProgress(props.task) && !isTaskBlocked(props.task)),
  { immediate: true }
);

const updateChecklist = async (args: { taskId: string; checklist: YardTaskChecklist[] }) => {
  const { checklist } = args;
  if (!props.task.relatedEntity) {
    return;
  }

  const newChild = {
    checklist,
    category: props.task.category || null,
    relatedEntity: props.task.relatedEntity,
    relatedId: props.task.relatedId || null
  };

  const taskId = props.task.id;
  if (!taskId) {
    return;
  }

  try {
    await updateTaskMutation.mutateAsync({
      taskId,
      params: {
        child: newChild
      }
    });
    notify(t('checkTaskDetails.checklist.success'), 'success');
  } catch {
    notify(t('checkTaskDetails.checklist.error'), 'error');
  }
};
</script>

<template>
  <div class="d-flex flex-column gap-2">
    <h3 class="d-flex align-center gap-2 py-2">
      <v-icon v-if="isUrgent" size="x-small" icon="mdi-flag" class="text--color-error-60" />
      {{ taskTypeLabel }}
    </h3>

    <check-task-details-title :task="props.task" />

    <v-card variant="flat" border>
      <v-card-text class="text-center">
        <task-meta :task="task" class="justify-center" />
      </v-card-text>
    </v-card>

    <div class="d-flex gap-2 flex-column flex-md-row">
      <v-card variant="flat" border class="flex-fill">
        <v-card-text class="d-flex flex-column gap-2">
          <h4>{{ t('checkTaskDetails.taskDescription.heading') }}</h4>
          <span class="typography-body-md">{{ taskDescription }}</span>
          <task-checklist
            v-if="taskChecklist && taskChecklist.length > 0"
            :is-loading="isLoading"
            :is-disabled="isLoading || !isTaskInProgress(props.task)"
            :checklist-items="taskChecklist"
            :task-id="props.task.id || ''"
            @update-checklist="updateChecklist" />
        </v-card-text>
      </v-card>

      <task-attachments :task-id="props.task.id" />
    </div>
  </div>
</template>
